import { useEffect, useRef } from 'react'
import { NextRouter } from 'next/router'

import { createVideoWatchEvent, prepareVideoEvent } from '~/behavior-tracking/utility/video-tracking.utility'
import { VideoEventType } from '~/behavior-tracking/types'
import ROUTES from '~/shared/constants/routes.const'

export function useVideoWatchTracker(router: NextRouter, userId: number | null): void {
  const pagePath = router.asPath

  const videoEvent = useRef<Event | null>(null)
  const videoDuration = useRef(0)
  const isPlayEvent = useRef(false)
  const lastPath = useRef('')

  useEffect(() => {
    const onPlay = async (e: Event): Promise<void> => {
      isPlayEvent.current = true
      videoEvent.current = e as Event
      const event = prepareVideoEvent(e, VideoEventType.PLAY, pagePath)
      const duration = event?.extra?.duration as number
      videoDuration.current = duration != null ? duration : 0
      lastPath.current = event?.viewKey as string

      if (event != null) {
        await createVideoWatchEvent(userId as number, event)
      }
    }

    const onPause = async (e: Event): Promise<void> => {
      isPlayEvent.current = false
      const event = prepareVideoEvent(e, VideoEventType.PAUSE, pagePath)

      if (event != null) {
        await createVideoWatchEvent(userId as number, event)
      }
    }

    const onSeeked = async (e: Event): Promise<void> => {
      isPlayEvent.current = false
      const event = prepareVideoEvent(e, VideoEventType.SEEK, pagePath)

      if (event != null) {
        await createVideoWatchEvent(userId as number, event)
      }
    }

    const onEnded = async (e: Event): Promise<void> => {
      isPlayEvent.current = false
      const event = prepareVideoEvent(e, VideoEventType.END, pagePath)

      if (event != null) {
        await createVideoWatchEvent(userId as number, event)
      }
    }

    const onRouteChangeStart = async (newPath: string) => {
      if (newPath !== pagePath) {
        const video = videoEvent.current?.target as HTMLVideoElement
        const currentTime = video?.currentTime
        // The video is being unmounted mid-play, so the browser won't fire a pause/seeked
        // event for the final segment. Emit a skip event now; prepareVideoEvent reads the
        // still-mounted element's native `video.played`, which already includes it.
        if (videoEvent.current && videoDuration.current > currentTime * 1000 && isPlayEvent.current) {
          const event = prepareVideoEvent(videoEvent.current, VideoEventType.SKIP_EVENT, pagePath)

          if (event != null) {
            await createVideoWatchEvent(userId as number, event)
          }
        }
      }
      isPlayEvent.current = false
      videoEvent.current = null
      videoDuration.current = 0
      lastPath.current = ''
    }

    if ((userId != null && pagePath.startsWith(ROUTES.BOOTCAMP)) || pagePath.startsWith(ROUTES.CURRICULUM)) {
      document.addEventListener('play', onPlay, true)
      document.addEventListener('pause', onPause, true)
      document.addEventListener('seeked', onSeeked, true)
      document.addEventListener('ended', onEnded, true)

      router.events.on('routeChangeStart', onRouteChangeStart)
    }

    return () => {
      document.removeEventListener('play', onPlay, true)
      document.removeEventListener('pause', onPause, true)
      document.removeEventListener('seeked', onSeeked, true)
      document.removeEventListener('ended', onEnded, true)

      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [pagePath, userId, router.events])
}
